import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";

type View = "overview" | "portfolio" | "accounts" | "outreach" | "finance" | "decisions" | "actions" | "pack";

type AccountSummary = {
  id: string;
  name: string;
  website: string;
  suburbsServed: string;
  portfolioSizeNote: string;
  status: string;
  notes: string;
  score: number;
  tier: string;
  scoredCategories: number;
  contactCount: number;
  stage: string;
  segment?: string;
};

type AccountScoreRecord = {
  accountId: string;
  category: string;
  rating: number;
  evidence: string;
  confidence: string;
  sourceUrl: string;
};

type AccountContact = {
  id: number;
  personName: string;
  role: string;
  email: string;
  phone: string;
  sourceUrl: string;
  notes: string;
};

type AccountBreakdown = {
  category: string;
  label: string;
  maxPoints: number;
  rating: number;
  weightedPoints: number;
};

type AccountActivity = {
  id: number;
  kind: string;
  subject: string;
  body: string;
  outcome: string;
  contactId: number | null;
  personName: string | null;
  createdBy: string;
  occurredAt: string;
};

type AccountDetail = AccountSummary & {
  breakdown: AccountBreakdown[];
  scores: AccountScoreRecord[];
  contacts: AccountContact[];
  activities: AccountActivity[];
};

const ACTIVITY_KINDS = ["email_sent", "call", "sms", "reply", "note"] as const;

const ACTIVITY_LABELS: Record<string, string> = {
  planned_email: "Prepared email",
  email_sent: "Email sent",
  call: "Call",
  sms: "SMS",
  reply: "Reply received",
  note: "Note",
};

function activityTone(kind: string): string {
  if (kind === "planned_email") return "amber";
  if (kind === "email_sent" || kind === "call" || kind === "sms") return "blue";
  if (kind === "reply") return "green";
  return "neutral";
}

const ACCOUNT_STATUSES = ["researching", "scored", "qualified", "excluded", "nurture"] as const;

function tierTone(tier: string): string {
  if (tier === "A") return "green";
  if (tier === "B") return "blue";
  if (tier === "C") return "amber";
  return "neutral";
}

const STAGE_LABELS: Record<string, string> = {
  research: "Research",
  ready: "Ready to send",
  emailed: "Emailed",
  called: "Called",
  replied: "Replied",
  parked: "Parked",
};

const STAGE_ORDER = ["replied", "called", "emailed", "ready", "research", "parked"] as const;

const STAGE_HINTS: Record<string, string> = {
  replied: "They answered. Respond and book the call.",
  called: "Phone contact made. Log the outcome and keep the sequence moving.",
  emailed: "Email 1 sent. Call on day 2 to 3; day-7 email if no reply.",
  ready: "Sequence loaded and address verified. Send email 1.",
  research: "No verified send path yet. Needs research before outreach.",
  parked: "Nurture or excluded. No active outreach.",
};

function stageTone(stage: string): string {
  if (stage === "replied") return "green";
  if (stage === "called") return "blue";
  if (stage === "emailed") return "blue";
  if (stage === "ready") return "amber";
  if (stage === "parked") return "red";
  return "neutral";
}

type ActionRecord = {
  id: string;
  action: string;
  owner: string;
  due: string;
  status: string;
  scope: string;
};

type DecisionRecord = {
  id: string;
  decision: string;
  owner: string;
  status: string;
  scope: string;
  decidedAt: string;
};

type PortfolioConnection = {
  status: "connected" | "needs_configuration" | "error";
  source: string;
  asOf: string | null;
  metrics: Record<string, number | null>;
  message: string;
};

type PortfolioResponse = {
  blingBling: PortfolioConnection;
  blackwood: PortfolioConnection;
};

function lastCompletedWeek(reference: Date): { start: Date; end: Date } {
  const end = new Date(reference);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() - (end.getDay() === 0 ? 7 : end.getDay()));
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return { start, end };
}

function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

const shortDate = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const longDate = new Intl.DateTimeFormat("en-AU", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const portfolioMetrics: Array<{
  company: string;
  monogram: string;
  tone: "amber" | "blue";
  system: string;
  status: string;
  metrics: Array<[string, string]>;
}> = [
  {
    company: "Bling Bling",
    monogram: "BB",
    tone: "amber",
    system: "CleaningPortal",
    status: "Adapter required",
    metrics: [
      ["New enquiries", "Pipeline"],
      ["Quote conversion", "Pipeline"],
      ["Collected revenue", "Commercial"],
      ["Gross margin", "Commercial"],
      ["Jobs completed", "Operations"],
      ["Rework rate", "Quality"],
    ],
  },
  {
    company: "Blackwood Barbers",
    monogram: "BW",
    tone: "blue",
    system: "Booking",
    status: "Adapter required",
    metrics: [
      ["Completed bookings", "Demand"],
      ["Completed revenue", "Commercial"],
      ["Forecast revenue", "Forward view"],
      ["Utilisation", "Operations"],
      ["No-show rate", "Quality"],
      ["Wage ratio", "Commercial"],
    ],
  },
];

const atlasMetrics = [
  "Target accounts mapped",
  "Valid decision-maker coverage",
  "Meaningful conversations",
  "Qualified pipeline value",
  "Opportunity-to-customer rate",
  "Active opportunities with a next action",
];

function Mark({ type }: { type: "atlas" | "bling" | "blackwood" }) {
  const labels = { atlas: "A", bling: "BB", blackwood: "BW" };
  return <span className={`mark mark-${type}`}>{labels[type]}</span>;
}

function StatusPill({ children, tone = "neutral" }: { children: React.ReactNode; tone?: string }) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

function ScoreEditor({
  entry,
  existing,
  onSave,
}: {
  entry: AccountBreakdown;
  existing?: AccountScoreRecord;
  onSave: (category: string, payload: { rating: number; evidence: string; confidence: string; sourceUrl: string }) => Promise<string>;
}) {
  const [rating, setRating] = useState(String(existing?.rating ?? 0));
  const [evidence, setEvidence] = useState(existing?.evidence ?? "");
  const [confidence, setConfidence] = useState(existing?.confidence ?? "low");
  const [sourceUrl, setSourceUrl] = useState(existing?.sourceUrl ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved" | string>("idle");

  async function save() {
    setState("saving");
    const problem = await onSave(entry.category, {
      rating: Number(rating),
      evidence: evidence.trim(),
      confidence,
      sourceUrl: sourceUrl.trim(),
    });
    setState(problem || "saved");
  }

  return (
    <div className="score-editor-row">
      <div className="score-editor-head">
        <strong>{entry.label}</strong>
        <span>
          {entry.weightedPoints.toFixed(1)} / {entry.maxPoints} pts
        </span>
      </div>
      <div className="score-editor-controls">
        <select className="field-input" aria-label={`${entry.label} rating`} value={rating} onChange={(event) => setRating(event.target.value)}>
          {[0, 1, 2, 3, 4, 5].map((value) => (
            <option key={value} value={value}>{value} / 5</option>
          ))}
        </select>
        <select className="field-input" aria-label={`${entry.label} confidence`} value={confidence} onChange={(event) => setConfidence(event.target.value)}>
          <option value="low">Low conf.</option>
          <option value="medium">Medium conf.</option>
          <option value="high">High conf.</option>
        </select>
        <input className="field-input" aria-label={`${entry.label} source URL`} placeholder="Source URL" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} />
        <button className="compact-button" type="button" disabled={state === "saving"} onClick={() => void save()}>
          {state === "saving" ? "Saving…" : "Save"}
        </button>
        <input className="field-input wide" aria-label={`${entry.label} evidence`} placeholder="Evidence — required for any non-zero rating" value={evidence} onChange={(event) => setEvidence(event.target.value)} />
      </div>
      {state !== "idle" && state !== "saving" && (
        <small style={{ color: state === "saved" ? "#24704f" : "#9f2a20" }}>
          {state === "saved" ? "Saved." : state}
        </small>
      )}
    </div>
  );
}

type FinanceReport = {
  company: string;
  months: Array<{ month: string; sales: number; expenses: number; profit: number; invoiceCount: number }>;
  receivables: Array<{ number: string; contact: string; date: string; total: number }>;
  quotePipeline: Array<{ status: string; count: number; value: number }>;
  latestExpenseDate: string | null;
  warnings: string[];
};

const aud = (n: number) => `$${n.toLocaleString("en-AU")}`;

type QueueRow = {
  id: number; accountId: string; name: string; recipient: string; status: string;
  scheduledFor: string; sentAt: string | null; error: string; subject: string; body: string; segment: string;
};
type ResearchRun = {
  id: number; segment: string; area: string; status: string; startedAt: string; finishedAt: string | null;
  candidates: number; admitted: number; rejected: number; duplicates: number; notes: string;
};
type ResearchReport = { runs: ResearchRun[]; weekly: Array<{ segment: string; weeklyTarget: number; admittedThisWeek: number }>; configured: boolean };
const SEGMENT_LABELS: Record<string, string> = { property_management: "Real estate", school: "Schools", childcare: "Childcare", commercial: "Commercial" };
const SEGMENT_ORDER = ["property_management", "school", "childcare", "commercial"];
type QueueReport = {
  queue: QueueRow[]; capToday: number; sentToday: number; hardCap: number; windowOpen: boolean; firstSentAt: string | null;
};

const bne = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "";
// Two days after the send, pushed to Monday when that lands on a weekend.
const callDay = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() + 2 * 86_400_000);
  const weekday = new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Brisbane", weekday: "short" }).format(d);
  const bump = weekday === "Sat" ? 2 : weekday === "Sun" ? 1 : 0;
  return new Date(d.getTime() + bump * 86_400_000).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "numeric", month: "short" });
};

function EmailPreview({ id, subject, recipient, apiFetch }: { id: number; subject: string; recipient: string; apiFetch: typeof fetch }) {
  const [html, setHtml] = useState<string | null>(null);
  useEffect(() => {
    apiFetch(`/api/outreach/preview/${id}`).then(async (r) => setHtml(r.ok ? await r.text() : "<p>Preview unavailable.</p>")).catch(() => setHtml("<p>Preview unavailable.</p>"));
  }, [apiFetch, id]);
  return (
    <div className="register-row single" style={{ background: "#fafaf8" }}>
      <div className="register-main">
        <p style={{ margin: "0 0 8px", fontSize: 13, color: "#555" }}><strong>Subject:</strong> {subject} · <strong>To:</strong> {recipient} · <strong>From:</strong> Bling Bling Brisbane &lt;info@blingblingbrisbane.com.au&gt; · Portfolio PDF attached</p>
        {html === null ? <p>Loading preview…</p> : (
          <iframe title={`Email preview ${id}`} srcDoc={html} sandbox="" style={{ width: "100%", height: 720, border: "1px solid #e4e4e0", borderRadius: 8, background: "#fff" }} />
        )}
      </div>
    </div>
  );
}

type PreviewRow = { row: number; name: string; agency: string; role: string; email: string; phone: string; suburbs: string; status: string; reason: string; existing: boolean; matched: string };
type Preview = { headers: string[]; mapping: Record<string, number>; rows: PreviewRow[]; summary: { total: number; ready: number; phoneOnly: number; invalid: number; alreadyInAtlas: number } };
type EditRow = PreviewRow & { emailEdit: string; phoneEdit: string; saved: boolean; saving: boolean; error: string };

const NA = "NA";
const isNa = (v: string) => v.trim() === "" || /^(na|n\/a|none|-)$/i.test(v.trim());
const looksLikeEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(v.trim());

function ImportPanel({ apiFetch, onImported }: { apiFetch: typeof fetch; onImported: () => void }) {
  const [rows, setRows] = useState<EditRow[]>([]);
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [mappingNote, setMappingNote] = useState("");

  const onFile = (file: File | undefined) => {
    if (!file) return;
    setBusy(true); setMessage(""); setRows([]); setFilename(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const data = String(reader.result).split(",")[1] ?? "";
      apiFetch("/api/import/preview", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ filename: file.name, data }) })
        .then(async (r) => {
          const body = (await r.json()) as Preview & { error?: string };
          if (!r.ok) throw new Error(body.error || "Could not read the file");
          const columns = Object.entries(body.mapping).map(([k, v]) => `${k} = "${body.headers[v]}"`).join(", ") || "none recognised";
          if (body.rows.length === 0) {
            setMessage(`Atlas read ${file.name} (${Math.round(file.size / 1024) || "<1"} KB) but found no contacts below the header row. Columns read: ${columns}. This is usually the blank template, or a file that was not saved after the contacts were pasted in. Save the file with the contacts in it, then choose it again.`);
            return;
          }
          setRows(body.rows.map((p) => ({ ...p, emailEdit: p.email || NA, phoneEdit: p.phone || NA, saved: false, saving: false, error: "" })));
          setMappingNote(`Columns read: ${columns}.`);
        })
        .catch((e) => setMessage(e instanceof Error ? e.message : "Could not read the file."))
        .finally(() => setBusy(false));
    };
    reader.readAsDataURL(file);
  };

  const update = (row: number, patch: Partial<EditRow>) => setRows((prev) => prev.map((r) => (r.row === row ? { ...r, ...patch } : r)));

  const save = (r: EditRow) => {
    const email = isNa(r.emailEdit) ? "" : r.emailEdit.trim().toLowerCase();
    const phone = isNa(r.phoneEdit) ? "" : r.phoneEdit.trim();
    if (email && !looksLikeEmail(email)) { update(r.row, { error: "That email does not look valid. Fix it or type NA." }); return; }
    if (!r.name && !r.agency) { update(r.row, { error: "This row has no name, so it cannot be saved." }); return; }
    update(r.row, { saving: true, error: "" });
    apiFetch("/api/import/commit", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ filename, segment: "property_management", rows: [{ row: r.row, name: r.name, agency: r.agency, role: r.role, email, phone, suburbs: r.suburbs }] }),
    })
      .then(async (resp) => {
        const body = (await resp.json()) as { created: number; willEmail: number; skipped: Array<{ row: number; reason: string }>; error?: string };
        if (!resp.ok) throw new Error(body.error || "Save failed");
        if (body.created === 0) { update(r.row, { saving: false, error: body.skipped[0]?.reason || "Not saved" }); return; }
        update(r.row, { saving: false, saved: true, email, phone, emailEdit: email || NA, phoneEdit: phone || NA, status: email ? "ready" : "phone_only", existing: false });
        onImported();
      })
      .catch((e) => update(r.row, { saving: false, error: e instanceof Error ? e.message : "Save failed" }));
  };

  const pending = rows.filter((r) => !r.saved && !r.existing && r.status !== "duplicate_in_file");
  // One request for the whole batch: saving 200 rows one by one hit the API
  // rate limit and raced on account ids (2 Nov 2026).
  const saveAll = () => {
    const batch: Array<{ row: number; name: string; agency: string; role: string; email: string; phone: string; suburbs: string }> = [];
    for (const r of pending) {
      if (r.saving) continue;
      const email = isNa(r.emailEdit) ? "" : r.emailEdit.trim().toLowerCase();
      const phone = isNa(r.phoneEdit) ? "" : r.phoneEdit.trim();
      if (email && !looksLikeEmail(email)) { update(r.row, { error: "That email does not look valid. Fix it or type NA." }); continue; }
      if (!r.name && !r.agency) { update(r.row, { error: "This row has no name, so it cannot be saved." }); continue; }
      batch.push({ row: r.row, name: r.name, agency: r.agency, role: r.role, email, phone, suburbs: r.suburbs });
    }
    if (batch.length === 0) return;
    const ids = new Set(batch.map((b) => b.row));
    setRows((prev) => prev.map((r) => (ids.has(r.row) ? { ...r, saving: true, error: "" } : r)));
    apiFetch("/api/import/commit", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ filename, segment: "property_management", rows: batch }),
    })
      .then(async (resp) => {
        const body = (await resp.json()) as { created: number; skipped: Array<{ row: number; reason: string }>; accounts: Array<{ row: number; email: string }>; error?: string };
        if (!resp.ok) throw new Error(body.error || "Save failed");
        const saved = new Map(body.accounts.map((a) => [a.row, a.email]));
        const skipped = new Map(body.skipped.map((s) => [s.row, s.reason]));
        setRows((prev) => prev.map((r) => {
          if (!ids.has(r.row)) return r;
          if (saved.has(r.row)) {
            const email = saved.get(r.row) ?? "";
            const phone = isNa(r.phoneEdit) ? "" : r.phoneEdit.trim();
            return { ...r, saving: false, saved: true, email, phone, emailEdit: email || NA, phoneEdit: phone || NA, status: email ? "ready" : "phone_only", existing: false };
          }
          return { ...r, saving: false, error: skipped.get(r.row) || "Not saved" };
        }));
        setMessage(`${body.created} saved, ${body.skipped.length} skipped.`);
        onImported();
      })
      .catch((e) => {
        const reason = e instanceof Error ? e.message : "Save failed";
        setRows((prev) => prev.map((r) => (ids.has(r.row) ? { ...r, saving: false, error: reason } : r)));
      });
  };

  return (
    <article className="panel register-panel">
      <div className="register-row single stage-heading"><div className="register-main" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
        <div><strong>Import contacts from the template</strong><p style={{ margin: 0 }}>Download the template, paste your contacts in (columns: Name, Agency, Email, Phone, Suburbs, Role), then choose the file. Each contact appears below; fix the email or phone if needed and press OK to save it. Duplicates of anyone already in Atlas (same email, same phone and name, or same name at the same agency) are flagged and cannot be saved.</p></div>
        <span style={{ display: "inline-flex", gap: 8 }}>
          <button className="compact-button" type="button" onClick={() => {
            apiFetch("/api/import/template.csv").then(async (r) => {
              const url = URL.createObjectURL(await r.blob());
              const link = document.createElement("a"); link.href = url; link.download = "atlas-contacts-template.csv"; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
            }).catch(() => setMessage("Could not download the template."));
          }}>Download template</button>
          <label className="compact-button" style={{ cursor: "pointer" }}>
            {busy ? "Reading…" : "Choose file"}
            <input type="file" accept=".xlsx,.csv" style={{ display: "none" }} disabled={busy} onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
        </span>
      </div></div>
      {message && <div className="register-row single"><div className="register-main"><p>{message}</p></div></div>}
      {rows.length > 0 && (
        <div className="register-row single"><div className="register-main" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
          <div><strong>{filename}: {rows.length} contacts, {rows.filter((r) => r.saved).length} saved, {pending.length} waiting for OK</strong><p style={{ margin: 0 }}>{mappingNote} Contacts with an email join the automated send queue once saved; contacts with NA email are loaded for calling only.</p></div>
          <div style={{ display: "flex", gap: 8 }}>
            {pending.length > 1 && <button className="compact-button" type="button" onClick={saveAll}>OK all remaining ({pending.length})</button>}
            <button className="compact-button" type="button" onClick={() => setRows([])}>Close</button>
          </div>
        </div></div>
      )}
      {rows.map((r) => (
        <div className="register-row import-row" key={r.row}>
          <span className="record-id">row {r.row}</span>
          <div className="register-main">
            <div className="import-fields">
              <div className="import-field"><label>Name</label><div className="import-static">{r.name || r.agency || "(no name)"}{r.agency && r.name ? <span className="import-sub"> · {r.agency}</span> : null}</div></div>
              <div className="import-field"><label>Email</label><input className="field-input" value={r.emailEdit} disabled={r.saved || r.existing || r.status === "duplicate_in_file"} onChange={(e) => update(r.row, { emailEdit: e.target.value, error: "" })} /></div>
              <div className="import-field"><label>Phone</label><input className="field-input" value={r.phoneEdit} disabled={r.saved || r.existing || r.status === "duplicate_in_file"} onChange={(e) => update(r.row, { phoneEdit: e.target.value, error: "" })} /></div>
            </div>
            {(r.error || (r.existing && !r.saved) || (!r.saved && r.reason)) && (
              <p className="import-note">{r.error || (r.existing ? `Duplicate: already in Atlas as ${r.matched}; not saved.` : r.reason)}</p>
            )}
          </div>
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            {r.saved ? <StatusPill tone="green">Saved{r.email ? ", will email" : ", call only"}</StatusPill>
              : r.existing ? <StatusPill tone="neutral">Duplicate</StatusPill>
              : r.status === "duplicate_in_file" ? <StatusPill tone="neutral">Duplicate in file</StatusPill>
              : <button className="primary-button" type="button" disabled={r.saving} onClick={() => save(r)}>{r.saving ? "Saving…" : "OK"}</button>}
          </span>
        </div>
      ))}
    </article>
  );
}

function OutreachView({ apiFetch }: { apiFetch: typeof fetch }) {
  const [report, setReport] = useState<QueueReport | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [notice, setNotice] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);
  const [research, setResearch] = useState<ResearchReport | null>(null);
  const [segmentTab, setSegmentTab] = useState<string>("property_management");

  const load = useCallback(() => {
    apiFetch("/api/research/runs").then(async (r) => { if (r.ok) setResearch((await r.json()) as ResearchReport); }).catch(() => undefined);
    apiFetch("/api/outreach/queue")
      .then(async (response) => {
        if (!response.ok) throw new Error();
        setReport((await response.json()) as QueueReport);
        setState("ready");
      })
      .catch(() => setState("error"));
  }, [apiFetch]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const cancel = (id: number) => {
    apiFetch(`/api/outreach/cancel/${id}`, { method: "POST" })
      .then((response) => { if (!response.ok) throw new Error(); setNotice(`Send ${id} cancelled.`); load(); })
      .catch(() => setNotice("Could not cancel that send."));
  };

  const inSegment = (r: QueueRow) => (r.segment ?? "property_management") === segmentTab;
  const queued = report?.queue.filter((r) => r.status === "queued" && inSegment(r)) ?? [];
  const sent = report?.queue.filter((r) => r.status === "sent" && inSegment(r)).slice().reverse() ?? [];
  const failed = report?.queue.filter((r) => r.status === "failed" && inSegment(r)) ?? [];
  const countFor = (seg: string) => report?.queue.filter((r) => (r.segment ?? "property_management") === seg && r.status !== "cancelled").length ?? 0;
  const weeklyFor = (seg: string) => research?.weekly.find((w) => w.segment === seg);
  const toneFor = (status: string) => (status === "sent" ? "green" : status === "failed" ? "red" : status === "cancelled" ? "neutral" : "amber");

  return (
    <section className="content-stack">
      <div className="section-heading account-toolbar">
        <div>
          <span className="eyebrow">AUTOMATED FIRST TOUCH · TUE TO THU FROM 9:00 AM BRISBANE · ONE PER MINUTE</span>
          <h2>{report ? `${report.sentToday} of ${report.capToday} sent today` : "Outreach queue"}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {report && <StatusPill tone={report.windowOpen ? "green" : "neutral"}>{report.windowOpen ? "Send window open" : "Outside send window"}</StatusPill>}
          <button className="compact-button" type="button" onClick={load}>Refresh</button>
        </div>
      </div>
      {notice && <div className="notice" role="status"><span>{notice}</span></div>}
      {state === "error" && <div className="notice notice-error" role="alert"><span>The outreach queue could not be loaded from the Atlas API.</span></div>}

      <div className="stage-tabs" role="tablist" aria-label="Segment">
        {SEGMENT_ORDER.map((seg) => (
          <button key={seg} className={`stage-tab${segmentTab === seg ? " active" : ""}`} type="button" role="tab" aria-selected={segmentTab === seg} onClick={() => setSegmentTab(seg)}>
            {SEGMENT_LABELS[seg]} <small>{countFor(seg)}</small>
          </button>
        ))}
      </div>

      <article className="panel register-panel">
        <div className="register-row single stage-heading"><div className="register-main"><strong>Research this week: {SEGMENT_LABELS[segmentTab]}</strong><p style={{ margin: 0 }}>
          {research
            ? research.configured
              ? `${weeklyFor(segmentTab)?.admittedThisWeek ?? 0} of ${weeklyFor(segmentTab)?.weeklyTarget ?? 0} new verified accounts found this week. The routine runs each weekday morning and rotates through Brisbane areas, widening outward when an area runs dry.`
              : "Research routine is built but waiting for the Anthropic API key to be set; no automatic research runs yet."
            : "Loading research status…"}
        </p></div></div>
        {(research?.runs ?? []).filter((r) => r.segment === segmentTab).slice(0, 5).map((run) => (
          <div className="register-row" key={run.id}>
            <span className="record-id">R-{String(run.id).padStart(3, "0")}</span>
            <div className="register-main"><strong>{run.area}</strong><p>{bne(run.startedAt)} · {run.candidates} found · {run.admitted} verified and loaded · {run.rejected} rejected (email not on cited page) · {run.duplicates} already known{run.notes ? ` · ${run.notes.slice(0, 160)}` : ""}</p></div>
            <StatusPill tone={run.status === "complete" ? "green" : run.status === "failed" ? "red" : "amber"}>{run.status}</StatusPill>
          </div>
        ))}
      </article>

      <article className="panel register-panel">
        <div className="register-row single stage-heading"><div className="register-main"><strong>Next to send ({queued.length})</strong><p style={{ margin: 0 }}>{report ? `Daily cap ${report.capToday} (ramping by one each week to ${report.hardCap}). Cancel anything that should not go.` : ""}</p></div></div>
        {queued.length === 0 && state === "ready" && <div className="register-row single"><div className="register-main"><p>Nothing queued. New Ready to send accounts are picked up automatically each minute during the window.</p></div></div>}
        {queued.map((row) => (
          <div key={row.id}>
            <div className="register-row">
              <span className="record-id">{row.accountId}</span>
              <div className="register-main">
                <strong>{row.name}</strong>
                <p>
                  <strong>{new Date(row.scheduledFor).getTime() <= Date.now() ? "Held (daily cap reached): sends next window" : `Sends ${bne(row.scheduledFor)}`}</strong>
                  {new Date(row.scheduledFor).getTime() <= Date.now() ? "" : " (Brisbane)"} · to {row.recipient} · "{row.subject}" · Veronica calls from {callDay(row.scheduledFor)}
                </p>
              </div>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <StatusPill tone="amber">Queued</StatusPill>
                <button className="compact-button" type="button" onClick={() => setOpenId(openId === row.id ? null : row.id)}>{openId === row.id ? "Hide email" : "View email"}</button>
                <button className="compact-button" type="button" onClick={() => cancel(row.id)}>Cancel</button>
              </span>
            </div>
            {openId === row.id && <EmailPreview id={row.id} subject={row.subject} recipient={row.recipient} apiFetch={apiFetch} />}
          </div>
        ))}
      </article>

      {failed.length > 0 && (
        <article className="panel register-panel">
          <div className="register-row single stage-heading"><div className="register-main"><strong>Failed ({failed.length})</strong><p style={{ margin: 0 }}>These did not send; the error is shown. Fix the address or ask Claude.</p></div></div>
          {failed.map((row) => (
            <div className="register-row" key={row.id}>
              <span className="record-id">{row.accountId}</span>
              <div className="register-main"><strong>{row.name}</strong><p>{row.recipient} · {row.error}</p></div>
              <StatusPill tone="red">Failed</StatusPill>
            </div>
          ))}
        </article>
      )}

      <article className="panel register-panel">
        <div className="register-row single stage-heading"><div className="register-main"><strong>Sent ({sent.length})</strong><p style={{ margin: 0 }}>Each send is also logged on the account; Veronica calls two to three days after the send time shown.</p></div></div>
        {sent.length === 0 && state === "ready" && <div className="register-row single"><div className="register-main"><p>No automated sends yet.</p></div></div>}
        {sent.map((row) => (
          <div key={row.id}>
            <div className="register-row">
              <span className="record-id">{row.accountId}</span>
              <div className="register-main"><strong>{row.name}</strong><p><strong>Sent {bne(row.sentAt)}</strong> (Brisbane) · to {row.recipient} · "{row.subject}" · call from {callDay(row.sentAt)}</p></div>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <StatusPill tone={toneFor(row.status)}>Sent</StatusPill>
                <button className="compact-button" type="button" onClick={() => setOpenId(openId === row.id ? null : row.id)}>{openId === row.id ? "Hide email" : "View email"}</button>
              </span>
            </div>
            {openId === row.id && <EmailPreview id={row.id} subject={row.subject} recipient={row.recipient} apiFetch={apiFetch} />}
          </div>
        ))}
      </article>
    </section>
  );
}

function FinanceView({ apiFetch }: { apiFetch: typeof fetch }) {
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback((sync: boolean) => {
    if (sync) setRefreshing(true);
    else setState("loading");
    apiFetch(`/api/finance${sync ? "?sync=1" : ""}`)
      .then(async (response) => {
        if (!response.ok) throw new Error();
        setReport((await response.json()) as FinanceReport);
        setState("ready");
      })
      .catch(() => setState("error"))
      .finally(() => setRefreshing(false));
  }, [apiFetch]);

  useEffect(() => { load(false); }, [load]);

  return (
    <section className="content-stack">
      <div className="section-heading account-toolbar">
        <div>
          <span className="eyebrow">STANDING BOARD ITEM</span>
          <h2>Bling Bling Brisbane finance</h2>
        </div>
        <button className="compact-button" type="button" disabled={refreshing} onClick={() => load(true)}>
          {refreshing ? "Refreshing from Xero…" : "Refresh from Xero"}
        </button>
      </div>

      {state === "error" && (
        <div className="notice notice-error" role="alert"><span>The finance report could not be loaded from the Atlas API.</span></div>
      )}
      {state === "loading" && (
        <article className="panel register-panel"><div className="register-row single"><div className="register-main"><p>Loading finance report…</p></div></div></article>
      )}

      {state === "ready" && report && (
        <>
          {report.warnings.map((warning) => (
            <div className="notice notice-error" role="alert" key={warning}><span>{warning}</span></div>
          ))}

          <article className="panel register-panel">
            <div className="register-row single stage-heading"><div className="register-main"><strong>Monthly result</strong><p style={{ margin: 0 }}>Sales are paid Xero invoices; expenses are categorised Xero bills; profit is the difference.</p></div></div>
            {report.months.map((row) => (
              <div className="register-row" key={row.month}>
                <span className="record-id">{row.month}</span>
                <div className="register-main">
                  <strong>{aud(row.sales)} sales · {aud(row.expenses)} expenses</strong>
                  <p>{row.invoiceCount} paid invoice{row.invoiceCount === 1 ? "" : "s"}</p>
                </div>
                <StatusPill tone={row.profit >= 0 ? "green" : "red"}>{row.profit >= 0 ? "+" : "-"}{aud(Math.abs(row.profit))}</StatusPill>
              </div>
            ))}
          </article>

          <article className="panel register-panel">
            <div className="register-row single stage-heading"><div className="register-main"><strong>Money to be collected</strong><p style={{ margin: 0 }}>Invoices issued and awaiting payment.</p></div></div>
            {report.receivables.length === 0 && (
              <div className="register-row single"><div className="register-main"><p>Nothing outstanding. Every issued invoice is paid.</p></div></div>
            )}
            {report.receivables.map((inv) => (
              <div className="register-row" key={inv.number}>
                <span className="record-id">{inv.number}</span>
                <div className="register-main"><strong>{inv.contact}</strong><p>Issued {inv.date}</p></div>
                <StatusPill tone="amber">{aud(inv.total)}</StatusPill>
              </div>
            ))}
          </article>

          <article className="panel register-panel">
            <div className="register-row single stage-heading"><div className="register-main"><strong>Quote pipeline, last 30 days</strong><p style={{ margin: 0 }}>Where new quote value stands right now.</p></div></div>
            {report.quotePipeline.map((row) => (
              <div className="register-row" key={row.status}>
                <span className="record-id">{String(row.count).padStart(2, "0")}</span>
                <div className="register-main"><strong>{row.status}</strong><p>{row.count} quote{row.count === 1 ? "" : "s"}</p></div>
                <StatusPill tone={row.status === "INVOICED" || row.status === "ACCEPTED" ? "green" : row.status === "DECLINED" ? "red" : "blue"}>{aud(row.value)}</StatusPill>
              </div>
            ))}
          </article>
        </>
      )}
    </section>
  );
}

function AccountsView({ apiFetch }: { apiFetch: typeof fetch }) {
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [notice, setNotice] = useState("");
  const [stageTab, setStageTab] = useState<string>("all");
  const [segmentFilter, setSegmentFilter] = useState<string>("all");
  const [showImport, setShowImport] = useState(false);
  const segOf = (a: AccountSummary) => a.segment ?? "property_management";
  const inSegment = (a: AccountSummary) => segmentFilter === "all" || segOf(a) === segmentFilter;

  const loadList = useCallback(() => {
    apiFetch("/api/accounts")
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setAccounts(data.accounts ?? []);
        setLoadError(false);
      })
      .catch(() => setLoadError(true));
  }, [apiFetch]);

  const loadDetail = useCallback(
    (id: string) => {
      apiFetch(`/api/accounts/${id}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => data && setDetail(data))
        .catch(() => undefined);
    },
    [apiFetch],
  );

  useEffect(() => loadList(), [loadList]);
  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  async function addAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const payload = {
      name: String(values.get("name") || "").trim(),
      website: String(values.get("website") || "").trim(),
      suburbsServed: String(values.get("suburbsServed") || "").trim(),
      portfolioSizeNote: String(values.get("portfolioSizeNote") || "").trim(),
      notes: String(values.get("notes") || "").trim(),
    };
    if (!payload.name) return;
    try {
      const response = await apiFetch("/api/accounts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json()) as { id?: string; error?: string };
      if (!response.ok) throw new Error(result.error || "Save failed");
      setShowAdd(false);
      setNotice(`${result.id} added to the target-account universe.`);
      loadList();
      setSelectedId(result.id ?? null);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "The account could not be saved.");
    }
  }

  async function saveScore(
    category: string,
    payload: { rating: number; evidence: string; confidence: string; sourceUrl: string },
  ): Promise<string> {
    if (!detail) return "No account selected";
    try {
      const response = await apiFetch(`/api/accounts/${detail.id}/scores/${category}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json()) as { error?: string };
      if (!response.ok) return result.error || "Save failed";
      loadDetail(detail.id);
      loadList();
      return "";
    } catch {
      return "The score could not be saved.";
    }
  }

  async function setStatus(status: string) {
    if (!detail) return;
    await apiFetch(`/api/accounts/${detail.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status }),
    }).catch(() => undefined);
    loadDetail(detail.id);
    loadList();
  }

  async function addContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail) return;
    const values = new FormData(event.currentTarget);
    const payload = {
      personName: String(values.get("personName") || "").trim(),
      role: String(values.get("role") || "").trim(),
      email: String(values.get("email") || "").trim(),
      phone: String(values.get("phone") || "").trim(),
      sourceUrl: String(values.get("sourceUrl") || "").trim(),
      notes: "",
    };
    const form = event.currentTarget;
    try {
      const response = await apiFetch(`/api/accounts/${detail.id}/contacts`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(result.error || "Save failed");
      form.reset();
      loadDetail(detail.id);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "The contact could not be saved.");
    }
  }

  async function removeContact(contactId: number) {
    if (!detail) return;
    await apiFetch(`/api/accounts/${detail.id}/contacts/${contactId}`, { method: "DELETE" }).catch(
      () => undefined,
    );
    loadDetail(detail.id);
  }

  async function logActivity(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail) return;
    const values = new FormData(event.currentTarget);
    const payload = {
      kind: String(values.get("kind") || "note"),
      subject: String(values.get("subject") || "").trim(),
      outcome: String(values.get("outcome") || "").trim(),
    };
    const form = event.currentTarget;
    try {
      const response = await apiFetch(`/api/accounts/${detail.id}/activities`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(result.error || "Save failed");
      form.reset();
      loadDetail(detail.id);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "The activity could not be saved.");
    }
  }

  return (
    <section className="content-stack">
      {notice && (
        <div className="notice" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice("")} aria-label="Dismiss notification">×</button>
        </div>
      )}
      {loadError && (
        <div className="notice notice-error" role="alert">
          <span>The target-account universe could not be loaded from the Atlas API.</span>
        </div>
      )}

      <div className="section-heading account-toolbar">
        <div>
          <span className="eyebrow">MARKET & ACCOUNT MAPPING</span>
          <h2>{accounts.length} Brisbane property-management accounts</h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="compact-button"
            type="button"
            onClick={() => {
              setNotice("Syncing with Gmail…");
              apiFetch("/api/gmail/sync", { method: "POST" })
                .then(async (response) => {
                  const result = (await response.json()) as {
                    newSends?: number; newReplies?: number; error?: string; errors?: string[];
                  };
                  if (!response.ok) throw new Error(result.error || "Sync failed");
                  setNotice(
                    `Gmail sync: ${result.newSends} new send${result.newSends === 1 ? "" : "s"}, ` +
                    `${result.newReplies} new repl${result.newReplies === 1 ? "y" : "ies"} linked` +
                    (result.errors?.length ? ` (${result.errors.length} lookups failed)` : ""),
                  );
                  loadList();
                  if (selectedId) loadDetail(selectedId);
                })
                .catch((error) => setNotice(error instanceof Error ? error.message : "Gmail sync failed."));
            }}
          >
            Sync Gmail
          </button>
          <button
            className="compact-button"
            type="button"
            onClick={() => {
              setNotice("Syncing softphone calls…");
              apiFetch("/api/calls/sync", { method: "POST" })
                .then(async (response) => {
                  const result = (await response.json()) as {
                    scannedCalls?: number; matched?: number; logged?: number; error?: string;
                  };
                  if (!response.ok) throw new Error(result.error || "Call sync failed");
                  setNotice(
                    `Call sync: ${result.logged} new call${result.logged === 1 ? "" : "s"} logged ` +
                    `(${result.matched} of ${result.scannedCalls} recent calls matched an account contact)`,
                  );
                  loadList();
                  if (selectedId) loadDetail(selectedId);
                })
                .catch((error) => setNotice(error instanceof Error ? error.message : "Call sync failed."));
            }}
          >
            Sync calls
          </button>
          <button
            className="compact-button"
            type="button"
            onClick={() => {
              setNotice("Scheduling first sends…");
              apiFetch("/api/outreach/schedule", { method: "POST" })
                .then(async (response) => {
                  const result = (await response.json()) as { queued?: number; firstSend?: string | null; dailyCap?: number; error?: string };
                  if (!response.ok) throw new Error(result.error || "Scheduling failed");
                  const first = result.firstSend
                    ? new Date(result.firstSend).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
                    : "n/a";
                  setNotice(
                    result.queued
                      ? `${result.queued} first send${result.queued === 1 ? "" : "s"} scheduled at ${result.dailyCap}/day (Tue to Thu, Brisbane); first goes ${first}. Veronica calls each account 2 to 3 days after its send.`
                      : "Nothing to schedule: every Ready to send account is already queued or contacted.",
                  );
                })
                .catch((error) => setNotice(error instanceof Error ? error.message : "Scheduling failed."));
            }}
          >
            Schedule first sends
          </button>
          <button
            className="compact-button"
            type="button"
            onClick={() => {
              setNotice("Sending due emails…");
              apiFetch("/api/outreach/run", { method: "POST" })
                .then(async (response) => {
                  const result = (await response.json()) as { sent?: number; failed?: number; error?: string };
                  if (!response.ok) throw new Error(result.error || "Send run failed");
                  setNotice(
                    `Send run: ${result.sent} sent${result.failed ? `, ${result.failed} failed` : ""}. ` +
                    `Outside the Tue to Thu 8am to 5pm Brisbane window nothing sends.`,
                  );
                  loadList();
                })
                .catch((error) => setNotice(error instanceof Error ? error.message : "Send run failed."));
            }}
          >
            Send due now
          </button>
          <button className={showImport ? "primary-button" : "compact-button"} type="button" onClick={() => setShowImport(!showImport)}>
            {showImport ? "Close import" : "Import contacts"}
          </button>
          <button
            className="compact-button"
            type="button"
            onClick={() => {
              setNotice("Preparing download…");
              apiFetch("/api/accounts/export/contacted.csv")
                .then(async (response) => {
                  if (!response.ok) throw new Error("Download failed");
                  const blob = await response.blob();
                  const name = response.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "atlas-contacted.csv";
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove();
                  URL.revokeObjectURL(url);
                  setNotice(`Downloaded ${name}: every contact emailed or called, one row each.`);
                })
                .catch((error) => setNotice(error instanceof Error ? error.message : "Download failed."));
            }}
          >
            Download contacted (CSV)
          </button>
          <button className="primary-button" type="button" onClick={() => setShowAdd(true)}>
            Add account
          </button>
        </div>
      </div>

      {showImport && <ImportPanel apiFetch={apiFetch} onImported={loadList} />}

      <div className="stage-tabs" role="tablist" aria-label="Segment">
        <button className={`stage-tab${segmentFilter === "all" ? " active" : ""}`} type="button" role="tab" aria-selected={segmentFilter === "all"} onClick={() => setSegmentFilter("all")}>
          All segments <small>{accounts.length}</small>
        </button>
        {SEGMENT_ORDER.map((seg) => {
          const count = accounts.filter((a) => segOf(a) === seg).length;
          return (
            <button key={seg} className={`stage-tab${segmentFilter === seg ? " active" : ""}`} type="button" role="tab" aria-selected={segmentFilter === seg} disabled={count === 0} onClick={() => setSegmentFilter(seg)}>
              {SEGMENT_LABELS[seg]} <small>{count}</small>
            </button>
          );
        })}
      </div>

      <div className="stage-tabs" role="tablist" aria-label="Outreach stage">
        <button
          className={`stage-tab${stageTab === "all" ? " active" : ""}`}
          type="button"
          role="tab"
          aria-selected={stageTab === "all"}
          onClick={() => setStageTab("all")}
        >
          All <small>{accounts.filter(inSegment).length}</small>
        </button>
        {STAGE_ORDER.map((stage) => {
          const count = accounts.filter((account) => inSegment(account) && account.stage === stage).length;
          return (
            <button
              key={stage}
              className={`stage-tab${stageTab === stage ? " active" : ""}`}
              type="button"
              role="tab"
              aria-selected={stageTab === stage}
              disabled={count === 0}
              onClick={() => setStageTab(stage)}
            >
              {STAGE_LABELS[stage]} <small>{count}</small>
            </button>
          );
        })}
      </div>

      <article className="panel register-panel">
        {accounts.length === 0 && !loadError && (
          <div className="register-row single"><div className="register-main"><strong>No accounts yet.</strong><p>Add the first researched account to begin the universe. Every non-zero rating requires evidence or a source.</p></div></div>
        )}
        {stageTab !== "all" && (
          <div className="register-row single stage-heading">
            <div className="register-main" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <StatusPill tone={stageTone(stageTab)}>{STAGE_LABELS[stageTab]}</StatusPill>
              <p style={{ margin: 0 }}>{STAGE_HINTS[stageTab]}</p>
            </div>
          </div>
        )}
        {accounts.filter(inSegment).filter((account) => stageTab === "all" || account.stage === stageTab).map((account) => (
          <div key={account.id}>
            <div className="register-row" style={{ cursor: "pointer" }} onClick={() => setSelectedId(account.id === selectedId ? null : account.id)}>
              <span className="record-id">{account.id}</span>
              <div className="register-main">
                <strong>{account.name}</strong>
                <p>
                  Score {account.score}/100 · {account.contactCount} contact{account.contactCount === 1 ? "" : "s"} · {account.scoredCategories}/8 rated · {account.status}
                  {account.suburbsServed ? ` · ${account.suburbsServed}` : ""}
                </p>
              </div>
              <span style={{ display: "inline-flex", gap: 6 }}>
                {stageTab === "all" && (
                  <StatusPill tone={stageTone(account.stage)}>{STAGE_LABELS[account.stage] ?? account.stage}</StatusPill>
                )}
                <StatusPill tone={tierTone(account.tier)}>Tier {account.tier}</StatusPill>
              </span>
            </div>
            {selectedId === account.id && !detail && (
              <div className="register-row single"><div className="register-main"><p>Loading account detail…</p></div></div>
            )}
            {selectedId === account.id && detail?.id === account.id && (
        <article className="panel account-detail" style={{ padding: "22px 24px" }}>
          <div className="panel-head">
            <div>
              <span className="eyebrow">{detail.id} · SCORE {detail.score}/100 · TIER {detail.tier}</span>
              <h3>{detail.name}</h3>
            </div>
            <select className="field-input" style={{ width: "auto" }} aria-label="Account status" value={detail.status} onChange={(event) => void setStatus(event.target.value)}>
              {ACCOUNT_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          {(detail.website || detail.portfolioSizeNote) && (
            <p className="panel-note">
              {detail.website && <>Website: {detail.website} · </>}
              {detail.portfolioSizeNote && <>Portfolio: {detail.portfolioSizeNote}</>}
            </p>
          )}

          <div>
            {detail.breakdown.map((entry) => (
              <ScoreEditor
                key={`${detail.id}-${entry.category}`}
                entry={entry}
                existing={detail.scores.find((score) => score.category === entry.category)}
                onSave={saveScore}
              />
            ))}
          </div>

          <div className="panel-head" style={{ marginTop: 22 }}>
            <div><span className="eyebrow">OUTREACH LOG</span><h3>Activity ({detail.activities.length})</h3></div>
          </div>
          <p className="panel-note">
            Prepared emails wait here until the founder-validation go is given. Log every
            send, call, and reply so nothing lives in memory.
          </p>
          {detail.activities.map((activity) => (
            <div className="activity-row" key={activity.id}>
              <div className="activity-head">
                <StatusPill tone={activityTone(activity.kind)}>{ACTIVITY_LABELS[activity.kind] ?? activity.kind}</StatusPill>
                <span className="activity-meta">
                  {shortDate.format(new Date(activity.occurredAt))}
                  {activity.personName ? ` · ${activity.personName}` : ""}
                </span>
              </div>
              {activity.subject && (
                <strong>
                  {activity.kind === "planned_email" || activity.kind === "email_sent"
                    ? `Subject: ${activity.subject}`
                    : activity.subject}
                </strong>
              )}
              {activity.outcome && <p>{activity.outcome}</p>}
              {activity.body && (
                <details>
                  <summary>View email body</summary>
                  <pre className="activity-body">{activity.body}</pre>
                </details>
              )}
            </div>
          ))}
          <form onSubmit={logActivity} className="activity-form">
            <select className="field-input" name="kind" aria-label="Activity type" defaultValue="email_sent">
              {ACTIVITY_KINDS.map((kind) => (
                <option key={kind} value={kind}>{ACTIVITY_LABELS[kind]}</option>
              ))}
            </select>
            <input className="field-input" name="subject" placeholder="Subject or short label (optional)" />
            <input className="field-input" name="outcome" placeholder="Outcome or note, e.g. spoke to reception, follow up Thursday" />
            <button className="compact-button" type="submit">Log activity</button>
          </form>

          <div className="panel-head" style={{ marginTop: 22 }}>
            <div><span className="eyebrow">DECISION-MAKERS</span><h3>Contacts ({detail.contacts.length})</h3></div>
          </div>
          <p className="panel-note">
            Publicly listed business contacts only, with the source recorded. Handle personal
            information in line with the Australian Privacy Principles; remove on request.
          </p>
          {detail.contacts.map((contact) => (
            <div className="contact-row" key={contact.id}>
              <div>
                <strong>{contact.personName}</strong>
                <p>{contact.role}{contact.email ? ` · ${contact.email}` : ""}{contact.phone ? ` · ${contact.phone}` : ""} · Source: {contact.sourceUrl}</p>
              </div>
              <button className="compact-button" type="button" onClick={() => void removeContact(contact.id)}>Remove</button>
            </div>
          ))}
          <form onSubmit={addContact} style={{ display: "grid", gap: 8, marginTop: 12 }}>
            <div className="form-grid">
              <input className="field-input" name="personName" placeholder="Name" required />
              <input className="field-input" name="role" placeholder="Role (e.g. Principal, Head of PM)" required />
            </div>
            <div className="form-grid">
              <input className="field-input" name="email" placeholder="Public business email (optional)" type="email" />
              <input className="field-input" name="phone" placeholder="Public business phone (optional)" />
            </div>
            <input className="field-input" name="sourceUrl" placeholder="Public source URL (required)" type="url" required />
            <div><button className="compact-button" type="submit">Add contact</button></div>
          </form>
        </article>
            )}
          </div>
        ))}
      </article>

      {showAdd && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setShowAdd(false)}>
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="account-modal-title">
            <div className="modal-head">
              <div><span className="eyebrow">TARGET ACCOUNT</span><h2 id="account-modal-title">Add a property-management account</h2></div>
              <button type="button" onClick={() => setShowAdd(false)} aria-label="Close dialog">×</button>
            </div>
            <form onSubmit={addAccount}>
              <label>Company name<input name="name" required /></label>
              <div className="form-grid">
                <label>Website<input name="website" type="url" placeholder="https://…" /></label>
                <label>Suburbs served<input name="suburbsServed" placeholder="e.g. Northside, Chermside, Aspley" /></label>
              </div>
              <label>Portfolio size note<input name="portfolioSizeNote" placeholder="e.g. ~450 managed properties (estimate, website claim)" /></label>
              <label>Notes<textarea name="notes" placeholder="Research notes, provenance, anything relevant" /></label>
              <div className="modal-actions">
                <button className="compact-button" type="button" onClick={() => setShowAdd(false)}>Cancel</button>
                <button className="primary-button" type="submit">Add account</button>
              </div>
            </form>
          </section>
        </div>
      )}
    </section>
  );
}

interface BoardPortalProps {
  apiFetch: typeof fetch;
  userEmail: string;
  onSignOut: () => Promise<void>;
}

export function BoardPortal({ apiFetch, userEmail, onSignOut }: BoardPortalProps) {
  const [view, setView] = useState<View>("overview");
  const [actions, setActions] = useState<ActionRecord[]>([]);
  const [decisions, setDecisions] = useState<DecisionRecord[]>([]);
  const [panel, setPanel] = useState<"action" | "decision" | null>(null);
  const [notice, setNotice] = useState("");
  const [registersError, setRegistersError] = useState(false);
  const [portfolioData, setPortfolioData] = useState<PortfolioResponse | null>(null);
  const [portfolioError, setPortfolioError] = useState(false);

  const period = useMemo(() => lastCompletedWeek(new Date()), []);

  useEffect(() => {
    apiFetch("/api/records")
      .then((response) => {
        if (!response.ok) throw new Error(`Records request failed with HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setActions(data.actions ?? []);
        setDecisions(data.decisions ?? []);
        setRegistersError(false);
      })
      .catch(() => setRegistersError(true));
  }, [apiFetch]);

  useEffect(() => {
    apiFetch(`/api/portfolio?from=${isoDate(period.start)}&to=${isoDate(period.end)}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Portfolio request failed with HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setPortfolioData(data);
        setPortfolioError(false);
      })
      .catch(() => setPortfolioError(true));
  }, [apiFetch, period]);

  const openActions = useMemo(
    () => actions.filter((item) => !["Done", "Cancelled", "Superseded"].includes(item.status)).length,
    [actions],
  );

  const connectionFor = (company: string) =>
    company === "Bling Bling" ? portfolioData?.blingBling : portfolioData?.blackwood;

  const connectionPill = (connection: PortfolioConnection | undefined, pendingLabel: string, liveLabel = "Live") => {
    if (connection?.status === "connected") return { tone: "green", label: liveLabel };
    if (portfolioError || connection?.status === "error") return { tone: "red", label: "Unavailable" };
    return { tone: "amber", label: pendingLabel };
  };

  const connectedSystems = [portfolioData?.blingBling, portfolioData?.blackwood].filter(
    (item) => item?.status === "connected",
  ).length;

  function formatMetric(name: string, value: number | null | undefined) {
    if (value == null) return "—";
    if (["Collected revenue", "Completed revenue", "Forecast revenue"].includes(name)) {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
      }).format(value);
    }
    if (["Quote conversion", "Gross margin", "Rework rate", "Utilisation", "No-show rate", "Wage ratio"].includes(name)) {
      return `${value.toFixed(1)}%`;
    }
    return new Intl.NumberFormat("en-AU").format(value);
  }

  async function addAction(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const record: ActionRecord = {
      id: "Pending",
      action: String(values.get("action") || "").trim(),
      owner: String(values.get("owner") || "").trim(),
      due: String(values.get("due") || "").trim(),
      status: "Open",
      scope: String(values.get("scope") || "FFP Holdings").trim(),
    };
    if (!record.action || !record.owner || !record.due) return;
    try {
      const response = await apiFetch("/api/records", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ type: "action", record }),
      });
      if (!response.ok) throw new Error("Save failed");
      const result = (await response.json()) as { id: string };
      const saved = { ...record, id: result.id };
      setActions((current) => [...current, saved]);
      setPanel(null);
      setNotice(`${saved.id} added to the board action register.`);
    } catch {
      setNotice("The action could not be saved. Please try again after the board database is available.");
    }
  }

  async function addDecision(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const record: DecisionRecord = {
      id: "Pending",
      decision: String(values.get("decision") || "").trim(),
      owner: String(values.get("owner") || "").trim(),
      scope: String(values.get("scope") || "FFP Holdings").trim(),
      status: "Approved",
      decidedAt: new Intl.DateTimeFormat("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date()),
    };
    if (!record.decision || !record.owner) return;
    try {
      const response = await apiFetch("/api/records", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ type: "decision", record }),
      });
      if (!response.ok) throw new Error("Save failed");
      const result = (await response.json()) as { id: string };
      const saved = { ...record, id: result.id };
      setDecisions((current) => [...current, saved]);
      setPanel(null);
      setNotice(`${saved.id} added to the board decision register.`);
    } catch {
      setNotice("The decision could not be saved. Please try again after the board database is available.");
    }
  }

  const nav: Array<[View, string, string]> = [
    ["overview", "Overview", "01"],
    ["portfolio", "Portfolio", "02"],
    ["accounts", "Accounts", "03"],
    ["outreach", "Outreach", "04"],
    ["finance", "Finance", "05"],
    ["decisions", "Decisions", "06"],
    ["actions", "Actions", String(openActions).padStart(2, "0")],
    ["pack", "Board pack", "06"],
  ];

  return (
    <div className="portal-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <Mark type="atlas" />
          <div>
            <strong>ATLAS</strong>
            <span>FFP Holdings</span>
          </div>
        </div>

        <nav aria-label="Board portal sections">
          <p className="nav-label">Board operating system</p>
          {nav.map(([id, label, count]) => (
            <button
              className={view === id ? "nav-item active" : "nav-item"}
              key={id}
              onClick={() => setView(id)}
              type="button"
            >
              <span>{label}</span>
              <small>{count}</small>
            </button>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="security-line"><span /> Private board workspace</div>
          <p>Portfolio data is minimised and separated by source system.</p>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div>
            <span className="eyebrow">FFP HOLDINGS / MONDAY BOARD</span>
            <h1>
              {view === "overview" && "Board overview"}
              {view === "portfolio" && "Portfolio performance"}
              {view === "accounts" && "Target accounts"}
              {view === "outreach" && "Outreach report"}
              {view === "finance" && "Finance report"}
              {view === "decisions" && "Decision register"}
              {view === "actions" && "Action register"}
              {view === "pack" && "Board pack"}
            </h1>
          </div>
          <div className="topbar-actions">
            <div className="period-control">
              <span>Reporting period</span>
              <strong>Week ending {shortDate.format(period.end)}</strong>
            </div>
            <button className="avatar" type="button" aria-label={`Sign out ${userEmail}`} title={`Signed in as ${userEmail}`} onClick={() => void onSignOut()}>
              {((userEmail.split("@")[0] ?? "").split(/[._-]/).map((part) => part.charAt(0)).join("").slice(0, 2) || "??").toUpperCase()}
            </button>
          </div>
        </header>

        {notice && (
          <div className="notice" role="status">
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice("")} aria-label="Dismiss notification">×</button>
          </div>
        )}

        {registersError && (
          <div className="notice notice-error" role="alert">
            <span>
              The board registers could not be loaded from the Atlas API. No records are shown
              until the connection is restored.
            </span>
          </div>
        )}

        {view === "overview" && (
          <section className="content-stack">
            <div className="briefing-card">
              <div className="briefing-copy">
                <span className="eyebrow light">WEEKLY CONTROL ROOM</span>
                <h2>One board view. Two operating systems. Clear decisions.</h2>
                <p>
                  Atlas brings governed reporting from CleaningPortal and Booking into
                  one holdings-level view without merging their operational records.
                </p>
              </div>
              <div className="briefing-meta">
                <div><span>Portfolio systems</span><strong>2</strong></div>
                <div><span>Open actions</span><strong>{openActions}</strong></div>
                <div><span>Decisions recorded</span><strong>{decisions.length}</strong></div>
              </div>
            </div>

              <div className="section-heading">
              <div><span className="eyebrow">PORTFOLIO PULSE</span><h2>What needs the board’s attention</h2></div>
              <StatusPill tone={connectedSystems === 2 ? "green" : "amber"}>{connectedSystems}/2 systems connected</StatusPill>
            </div>

            <div className="portfolio-grid">
              {portfolioMetrics.map((company) => (
                <article className={`portfolio-card card-${company.tone}`} key={company.company}>
                  <div className="card-head">
                    <div className="company-title">
                      <Mark type={company.tone === "amber" ? "bling" : "blackwood"} />
                      <div><h3>{company.company}</h3><p>Source: {company.system}</p></div>
                    </div>
                    <StatusPill tone={connectionPill(connectionFor(company.company), company.status).tone}>
                      {connectionPill(connectionFor(company.company), company.status).label}
                    </StatusPill>
                  </div>
                  <div className="metric-preview">
                    {company.metrics.slice(0, 4).map(([name, group]) => (
                      <div key={name}>
                        <span>{group}</span>
                        <strong>{formatMetric(name, connectionFor(company.company)?.metrics[name])}</strong>
                        <p>{name}</p>
                      </div>
                    ))}
                  </div>
                  <button className="text-button" type="button" onClick={() => setView("portfolio")}>
                    Review metric contract <span>→</span>
                  </button>
                </article>
              ))}
            </div>

            <div className="lower-grid">
              <article className="panel">
                <div className="panel-head"><div><span className="eyebrow">BOARD ACTIONS</span><h3>Next accountable moves</h3></div><button className="compact-button" type="button" onClick={() => setView("actions")}>View all</button></div>
                <div className="action-list">
                  {actions.slice(0, 3).map((action) => (
                    <div className="action-row" key={action.id}>
                      <span className="record-id">{action.id}</span>
                      <div><strong>{action.action}</strong><p>{action.owner} · {action.due}</p></div>
                      <StatusPill tone={action.status === "In progress" ? "blue" : "amber"}>{action.status}</StatusPill>
                    </div>
                  ))}
                </div>
              </article>

              <article className="panel readiness-panel">
                <div className="panel-head"><div><span className="eyebrow">DATA READINESS</span><h3>Connection boundary</h3></div><span className="readiness-score">{connectedSystems}/2</span></div>
                <div className="connection-line"><Mark type="bling" /><div><strong>CleaningPortal</strong><p>{portfolioError ? "The Atlas API could not be reached" : portfolioData?.blingBling.message || "Checking board reporting adapter"}</p></div><StatusPill tone={connectionPill(portfolioData?.blingBling, "Pending").tone}>{connectionPill(portfolioData?.blingBling, "Pending").label}</StatusPill></div>
                <div className="connection-line"><Mark type="blackwood" /><div><strong>Booking</strong><p>{portfolioError ? "The Atlas API could not be reached" : portfolioData?.blackwood.message || "Checking board reporting adapter"}</p></div><StatusPill tone={connectionPill(portfolioData?.blackwood, "Pending").tone}>{connectionPill(portfolioData?.blackwood, "Pending").label}</StatusPill></div>
                <p className="panel-note">No customer-level data is requested or shown in the board layer.</p>
              </article>
            </div>
          </section>
        )}

        {view === "portfolio" && (
          <section className="content-stack">
            <div className="section-heading">
              <div><span className="eyebrow">APPROVED KPI CONTRACT</span><h2>Metrics mapped to real portfolio sources</h2></div>
              <StatusPill tone="blue">Definitions available</StatusPill>
            </div>
            <div className="portfolio-grid">
              {portfolioMetrics.map((company) => (
                <article className="panel portfolio-detail" key={company.company}>
                  <div className="card-head">
                    <div className="company-title"><Mark type={company.tone === "amber" ? "bling" : "blackwood"} /><div><h3>{company.company}</h3><p>{company.system} adapter</p></div></div>
                    <StatusPill tone={connectionPill(connectionFor(company.company), "Awaiting credential").tone}>{connectionPill(connectionFor(company.company), "Awaiting credential", "Live aggregate data").label}</StatusPill>
                  </div>
                  <div className="definition-table" role="table" aria-label={`${company.company} metrics`}>
                    {company.metrics.map(([metric, category]) => (
                      <div className="definition-row" role="row" key={metric}>
                        <div role="cell"><strong>{metric}</strong><span>{category}</span></div>
                        <span className="source-tag" role="cell">{company.system}</span>
                        <span className="empty-value" role="cell">{formatMetric(metric, connectionFor(company.company)?.metrics[metric])}</span>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
            <article className="panel">
              <div className="panel-head"><div><span className="eyebrow">ATLAS BUSINESS DEVELOPMENT</span><h3>Holdings-level acquisition measures</h3></div><StatusPill tone="neutral">Atlas-owned</StatusPill></div>
              <div className="atlas-metric-grid">
                {atlasMetrics.map((metric) => <div key={metric}><strong>—</strong><span>{metric}</span><small>Awaiting first governed submission</small></div>)}
              </div>
            </article>
          </section>
        )}

        {view === "accounts" && <AccountsView apiFetch={apiFetch} />}
        {view === "outreach" && <OutreachView apiFetch={apiFetch} />}
        {view === "finance" && <FinanceView apiFetch={apiFetch} />}

        {view === "decisions" && (
          <section className="content-stack">
            <div className="section-heading"><div><span className="eyebrow">GOVERNANCE RECORD</span><h2>{decisions.length} decision{decisions.length === 1 ? "" : "s"} preserved</h2></div><button className="primary-button" onClick={() => setPanel("decision")} type="button">Record decision</button></div>
            <article className="panel register-panel">
              {decisions.map((decision) => (
                <div className="register-row" key={decision.id}>
                  <span className="record-id">{decision.id}</span>
                  <div className="register-main"><strong>{decision.decision}</strong><p>{decision.scope} · {decision.decidedAt} · Owner: {decision.owner}</p></div>
                  <StatusPill tone="green">{decision.status}</StatusPill>
                </div>
              ))}
            </article>
          </section>
        )}

        {view === "actions" && (
          <section className="content-stack">
            <div className="section-heading"><div><span className="eyebrow">ACCOUNTABILITY</span><h2>{openActions} open board actions</h2></div><button className="primary-button" onClick={() => setPanel("action")} type="button">Add action</button></div>
            <article className="panel register-panel">
              {actions.map((action) => (
                <div className="register-row" key={action.id}>
                  <span className="record-id">{action.id}</span>
                  <div className="register-main"><strong>{action.action}</strong><p>{action.scope} · Owner: {action.owner} · Due: {action.due}</p></div>
                  <StatusPill tone={action.status === "In progress" ? "blue" : "amber"}>{action.status}</StatusPill>
                </div>
              ))}
            </article>
          </section>
        )}

        {view === "pack" && (
          <section className="content-stack">
            <div className="pack-hero">
              <div><span className="eyebrow light">{longDate.format(new Date()).toUpperCase()}</span><h2>FFP Holdings weekly board pack</h2><p>Version 0.1 · Internal board · Reporting period ending {shortDate.format(period.end)}</p></div>
              <button className="light-button" type="button" onClick={() => setNotice("Board pack preparation checklist opened for this reporting cycle.")}>Prepare pack</button>
            </div>
            <div className="pack-grid">
              {["Chair summary", "Prior decisions & actions", "Holdings dashboard", "Bling Bling", "Blackwood Barbers", "Atlas & shared capabilities", "Decision papers", "Risk & compliance"].map((section, index) => (
                <article className="pack-section" key={section}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{section}</strong><p>{index < 2 ? "Ready from current registers" : "Awaiting governed submission"}</p></div><StatusPill tone={index < 2 ? "green" : "neutral"}>{index < 2 ? "Ready" : "Pending"}</StatusPill></article>
              ))}
            </div>
          </section>
        )}
      </main>

      {panel && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setPanel(null)}>
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
            <div className="modal-head"><div><span className="eyebrow">BOARD REGISTER</span><h2 id="modal-title">{panel === "action" ? "Add an accountable action" : "Record a board decision"}</h2></div><button type="button" onClick={() => setPanel(null)} aria-label="Close dialog">×</button></div>
            {panel === "action" ? (
              <form onSubmit={addAction}>
                <label>Required outcome<textarea name="action" required placeholder="Describe the completed outcome, not just the activity" /></label>
                <div className="form-grid"><label>Accountable owner<input name="owner" required /></label><label>Due date or trigger<input name="due" required placeholder="e.g. Before first pack" /></label></div>
                <label>Scope<input name="scope" defaultValue="FFP Holdings" /></label>
                <div className="modal-actions"><button className="compact-button" type="button" onClick={() => setPanel(null)}>Cancel</button><button className="primary-button" type="submit">Add action</button></div>
              </form>
            ) : (
              <form onSubmit={addDecision}>
                <label>Exact decision<textarea name="decision" required placeholder="State what was approved, rejected, or deferred" /></label>
                <div className="form-grid"><label>Decision owner<input name="owner" required /></label><label>Scope<input name="scope" defaultValue="FFP Holdings" /></label></div>
                <div className="modal-actions"><button className="compact-button" type="button" onClick={() => setPanel(null)}>Cancel</button><button className="primary-button" type="submit">Record decision</button></div>
              </form>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
